import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  getMe,
  getAuthToken,
} from '@recipe/shared';
import { toast } from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize and check current user from token
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = await getAuthToken();
        if (token) {
          const res = await getMe();
          if (res?.user) {
            // Include uid alias for compatibility with code expecting firebaseUser.uid
            const formatted = {
              ...res.user,
              uid: res.user.id,
            };
            setUser(formatted);
          }
        }
      } catch (err) {
        console.warn('Failed to restore session:', err);
        await apiLogout();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await apiLogin(email, password);
      const formatted = {
        ...res.user,
        uid: res.user.id,
      };
      setUser(formatted);
      toast.success('登录成功！');
      return true;
    } catch (err) {
      toast.error(err.message || '登录失败');
      return false;
    }
  };

  const register = async (email, password, displayName) => {
    try {
      const res = await apiRegister(email, password, displayName);
      const formatted = {
        ...res.user,
        uid: res.user.id,
      };
      setUser(formatted);
      toast.success('注册成功！');
      return true;
    } catch (err) {
      toast.error(err.message || '注册失败');
      return false;
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
      setUser(null);
      toast.success('已退出登录');
    } catch (err) {
      toast.error('退出登录失败');
    }
  };

  const isAuthorized = Boolean(user && user.isAllowed);
  const isAdmin = Boolean(user && user.isAdmin);

  const value = {
    user,
    currentUser: user, // Alias for files using currentUser
    loading,
    isAuthorized,
    isAdmin,
    login,
    register,
    signInWithEmail: login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
